import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { createApiClient } from "../api/client";
import { Family, SessionSummaryDto } from "../types";
import { statusBadge } from "../utils/sessionStatus";
import Button from "../components/ui/Button";
import Reveal from "../components/motion/Reveal";
import StartPlanFlow from "../components/StartPlanFlow";

const PlansPage: React.FC = () => {
  const { familyId = "" } = useParams();
  const { getAccessTokenSilently } = useAuth0();
  const [family, setFamily] = useState<Family | null>(null);
  const [plans, setPlans] = useState<SessionSummaryDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [startOpen, setStartOpen] = useState(false);
  const [showCancelled, setShowCancelled] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      const api = createApiClient(token);
      const [fam, sessions] = await Promise.all([
        api.get<Family>(`/api/families/${familyId}`),
        api.get<SessionSummaryDto[]>(`/api/families/${familyId}/sessions`),
      ]);
      setFamily(fam.data);
      setPlans(sessions.data);
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, familyId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-primary-500" />
      </div>
    );
  }

  const cancelledCount = plans.filter((p) => p.state === "Cancelled").length;
  const visible = showCancelled ? plans : plans.filter((p) => p.state !== "Cancelled");

  return (
    <div className="mx-auto max-w-3xl">
      <Link to="/dashboard" className="text-sm text-ink/55 hover:text-ink">
        ‹ Families
      </Link>

      <div className="mt-2 flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-semibold text-ink">{family?.name ?? "Plans"}</h1>
          <p className="mt-1 text-ink/60">Tonight's and past plans.</p>
        </div>
        <Button className="shrink-0" onClick={() => setStartOpen(true)}>
          Start a plan
        </Button>
      </div>

      {cancelledCount > 0 && (
        <label className="mt-4 flex cursor-pointer select-none items-center gap-2 text-sm text-ink/55">
          <input
            type="checkbox"
            checked={showCancelled}
            onChange={(e) => setShowCancelled(e.target.checked)}
            className="h-4 w-4 rounded border-primary-300 text-primary-500 focus:ring-primary-300"
          />
          Show cancelled ({cancelledCount})
        </label>
      )}

      {visible.length === 0 ? (
        <p className="mt-12 text-center text-ink/50">
          {plans.length === 0 ? "No plans yet — start one above." : "No active plans."}
        </p>
      ) : (
        <div className="mt-6 space-y-3">
          {visible.map((p, i) => {
            const badge = statusBadge(p.state);
            const isCancelled = p.state === "Cancelled";
            return (
              <Reveal key={p.id} index={i}>
                <Link
                  to={`/sessions/${p.id}`}
                  className={`block rounded-3xl border border-primary-100 bg-white/70 p-5 shadow-warm backdrop-blur-sm transition-transform duration-300 hover:-translate-y-0.5 ${
                    isCancelled ? "opacity-60" : ""
                  }`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-display text-lg font-semibold text-ink">
                      {formatDate(p.targetDate)}
                    </span>
                    <span className={`rounded-full px-3 py-1 text-xs font-semibold ${badge.cls}`}>
                      {badge.label}
                    </span>
                  </div>
                  <p className="mt-1.5 text-sm text-ink/60">
                    {p.decisionTitle ? (
                      <>
                        Chose <span className="font-medium text-ink/80">{p.decisionTitle}</span>
                      </>
                    ) : (
                      `${p.optedInCount} of ${p.participantCount} in`
                    )}
                  </p>
                  {p.averageRating != null && (p.ratingCount ?? 0) > 0 && (
                    <p className="mt-1 text-xs font-medium text-primary-600">
                      ★ {p.averageRating.toFixed(1)} ({p.ratingCount})
                    </p>
                  )}
                </Link>
              </Reveal>
            );
          })}
        </div>
      )}

      <StartPlanFlow
        open={startOpen}
        onClose={() => {
          setStartOpen(false);
          load();
        }}
        initialFamilyId={familyId}
      />
    </div>
  );
};

function formatDate(iso: string): string {
  // iso is "yyyy-MM-dd"
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

export default PlansPage;
